import { useParams, Navigate, Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Seo, Eyebrow, CTAButton } from "@/components/site/primitives";
import { Reveal, Stagger, Item } from "@/components/site/motion";
import { PageHero, Section } from "@/components/site/pageparts";
import { CopyInstall } from "@/components/site/CopyInstall";
import { SOLUTIONS_CONTENT } from "@/data/solutions";

export default function SolutionDetail() {
  const { slug } = useParams();
  const s = SOLUTIONS_CONTENT[slug];
  if (!s) return <Navigate to="/solutions" replace />;

  return (
    <div data-testid="solution-detail-page">
      <Seo title={`${s.name} — Solutions`} description={s.lead} />
      <PageHero eyebrow={s.eyebrow} title={s.lead}>
        <CTAButton to="/company/contact" variant="solid">
          Talk to us <ArrowRight className="h-4 w-4" />
        </CTAButton>
        {s.productLink && (
          <CTAButton to={s.productLink} variant="outline">
            {s.productLabel} <ArrowUpRight className="h-4 w-4" />
          </CTAButton>
        )}
      </PageHero>

      <Section>
        <div className="grid gap-12 lg:grid-cols-2">
          <Reveal>
            <Eyebrow>The problem</Eyebrow>
            <p className="mt-6 font-serif text-xl leading-[1.6] text-bone/90 md:text-2xl">{s.problem}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <Eyebrow>What changes</Eyebrow>
            <p className="mt-6 text-[17px] leading-relaxed text-mist">{s.change}</p>
          </Reveal>
        </div>
      </Section>

      <Section className="border-t border-white/10">
        <Reveal>
          <Eyebrow>Use cases</Eyebrow>
          <h2 className="mt-5 font-serif text-3xl tracking-tighter text-bone md:text-5xl">Where it earns its place.</h2>
        </Reveal>
        <Stagger className="mt-14 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2">
          {s.useCases.map((u) => (
            <Item key={u.title} className="bg-ink p-8">
              <h3 className="font-serif text-xl text-bone">{u.title}</h3>
              <p className="mt-3 text-[15px] leading-relaxed text-mist">{u.body}</p>
            </Item>
          ))}
        </Stagger>
      </Section>

      {s.devInstall && (
        <Section className="border-t border-white/10">
          <div className="grid gap-10 lg:grid-cols-2">
            <Reveal>
              <Eyebrow>Build on it</Eyebrow>
              <h2 className="mt-5 font-serif text-3xl tracking-tighter text-bone md:text-4xl">Start in one command.</h2>
              <p className="mt-6 max-w-md text-[15px] leading-relaxed text-mist">
                The open-source core is MIT-licensed and live on PyPI. Install the engine, point it at a folder, and
                query cited memory.
              </p>
            </Reveal>
            <Reveal delay={0.1} className="max-w-md">
              <CopyInstall variant="block" />
            </Reveal>
          </div>
        </Section>
      )}

      <Section className="border-t border-white/10">
        <Reveal className="max-w-3xl">
          <Eyebrow>Compliance</Eyebrow>
          <p className="mt-6 text-[17px] leading-relaxed text-mist">{s.compliance}</p>
          {s.sema && (
            <p className="mt-8 border-l-2 border-saffron/50 pl-5 text-sm leading-relaxed text-mist">
              {s.sema} <Link to="/products/semalayer" className="link-underline pb-0.5 text-bone hover:text-saffron">Explore Semalayer →</Link>
            </p>
          )}
        </Reveal>
      </Section>

      <Section className="border-t border-white/10">
        <Reveal className="flex flex-col items-start justify-between gap-8 md:flex-row md:items-center">
          <h2 className="max-w-xl text-balance font-serif text-3xl leading-tight tracking-tighter text-bone md:text-4xl">
            Bring your hardest recall problem.
          </h2>
          <div className="flex flex-wrap gap-4">
            <CTAButton to="/company/contact" variant="solid">Talk to us <ArrowRight className="h-4 w-4" /></CTAButton>
            <CTAButton to="/solutions" variant="outline">All sectors</CTAButton>
          </div>
        </Reveal>
      </Section>
    </div>
  );
}
